import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import LiveChat from '../../components/Chat/LiveChat';
import BlogSection from '../../components/Blog/BlogSection';
import LanguageSelector from '../../components/LanguageSelector/LanguageSelector';
import FloatingNav from '../../components/Navigation/FloatingNav';

const fadeInUp = {
  initial: { opacity: 0, y: 60 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

const LandingPage = () => {
  const { t } = useTranslation();
  return (
    <div className="min-h-screen bg-white">
      {/* Header avec navigation */}
      <header className="absolute inset-x-0 top-0 z-50">
        <nav className="mx-auto flex items-center justify-between p-6 lg:px-8" aria-label="Global">
          <div className="flex lg:flex-1">
            <a href="#" className="-m-1.5 p-1.5">
              <span className="sr-only">Your Company</span>
              <img
                className="h-8 w-auto"
                src="/logo.png"
                alt="Logo"
              />
            </a>
          </div>
          
          <div className="flex items-center gap-x-6">
            <LanguageSelector />
            <Link
              to="/login"
              className="text-sm font-semibold leading-6 text-gray-900 hover:text-gray-600"
            >
              {t('header.login')} <span aria-hidden="true">&rarr;</span>
            </Link>
            <Link
              to="/register"
              className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              {t('header.register')}
            </Link>
          </div>
        </nav>
      </header>

      {/* Hero Section */}
      <div id="hero" className="relative isolate px-6 pt-14 lg:px-8">
        <div className="mx-auto max-w-2xl py-32 sm:py-48 lg:py-56">
          <div className="text-center">
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              {t('hero.title')}
            </h1>
            <p className="mt-6 text-lg leading-8 text-gray-600">
              {t('hero.description')}
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/register"
                className="rounded-md bg-indigo-600 px-3.5 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                {t('hero.cta.start')}
              </Link>
              <Link
                to="/pricing"
                className="text-sm font-semibold leading-6 text-gray-900"
              >
                {t('hero.cta.pricing')} <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <div id="features" className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600">
              {t('features.title')}
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t('features.subtitle')}
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-3">
              <div className="flex flex-col">
                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                  {t('features.items.auto.title')}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                  <p className="flex-auto">
                    {t('features.items.auto.description')}
                  </p>
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                  {t('features.items.tracking.title')}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                  <p className="flex-auto">
                    {t('features.items.tracking.description')}
                  </p>
                </dd>
              </div>
              <div className="flex flex-col">
                <dt className="flex items-center gap-x-3 text-base font-semibold leading-7 text-gray-900">
                  {t('features.items.templates.title')}
                </dt>
                <dd className="mt-4 flex flex-auto flex-col text-base leading-7 text-gray-600">
                  <p className="flex-auto">
                    {t('features.items.templates.description')}
                  </p>
                </dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Stats Section */}
      <div id="stats" className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <dl className="grid grid-cols-1 gap-x-8 gap-y-16 text-center lg:grid-cols-3">
            <div className="mx-auto flex max-w-xs flex-col gap-y-4">
              <dt className="text-base leading-7 text-gray-600">{t('stats.title')}</dt>
              <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900 sm:text-5xl">95%</dd>
            </div>
            <div className="mx-auto flex max-w-xs flex-col gap-y-4">
              <dt className="text-base leading-7 text-gray-600">{t('stats.reduction.title')}</dt>
              <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900 sm:text-5xl">75%</dd>
            </div>
            <div className="mx-auto flex max-w-xs flex-col gap-y-4">
              <dt className="text-base leading-7 text-gray-600">{t('stats.return.title')}</dt>
              <dd className="order-first text-3xl font-semibold tracking-tight text-gray-900 sm:text-5xl">50%</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* Testimonials Section */}
      <div id="testimonials" className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-xl text-center">
            <h2 className="text-lg font-semibold leading-8 tracking-tight text-indigo-600">{t('testimonials.title')}</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t('testimonials.subtitle')}
            </p>
          </div>
          <div className="mx-auto mt-16 flow-root max-w-2xl sm:mt-20 lg:mx-0 lg:max-w-none">
            <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-3">
              <figure className="rounded-2xl bg-white p-8 shadow-lg ring-1 ring-gray-900/5">
                <blockquote className="text-gray-900">
                  <p>{t('testimonials.items.item1')}</p>
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-x-4">
                  <div>
                    <div className="font-semibold">{t('testimonials.items.item1.author')}</div>
                    <div className="text-gray-600">{t('testimonials.items.item1.position')}</div>
                  </div>
                </figcaption>
              </figure>
              <figure className="rounded-2xl bg-white p-8 shadow-lg ring-1 ring-gray-900/5">
                <blockquote className="text-gray-900">
                  <p>{t('testimonials.items.item2')}</p>
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-x-4">
                  <div>
                    <div className="font-semibold">{t('testimonials.items.item2.author')}</div>
                    <div className="text-gray-600">{t('testimonials.items.item2.position')}</div>
                  </div>
                </figcaption>
              </figure>
              <figure className="rounded-2xl bg-white p-8 shadow-lg ring-1 ring-gray-900/5">
                <blockquote className="text-gray-900">
                  <p>{t('testimonials.items.item3')}</p>
                </blockquote>
                <figcaption className="mt-6 flex items-center gap-x-4">
                  <div>
                    <div className="font-semibold">{t('testimonials.items.item3.author')}</div>
                    <div className="text-gray-600">{t('testimonials.items.item3.position')}</div>
                  </div>
                </figcaption>
              </figure>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ Section */}
      <div className="bg-white py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600">{t('faq.title')}</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t('faq.subtitle')}
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <dl className="grid max-w-xl grid-cols-1 gap-x-8 gap-y-16 lg:max-w-none lg:grid-cols-2">
              <div>
                <dt className="font-semibold text-gray-900">{t('faq.items.item1.title')}</dt>
                <dd className="mt-4 text-gray-600">{t('faq.items.item1.description')}</dd>
              </div>
              <div>
                <dt className="font-semibold text-gray-900">{t('faq.items.item2.title')}</dt>
                <dd className="mt-4 text-gray-600">{t('faq.items.item2.description')}</dd>
              </div>
              <div>
                <dt className="font-semibold text-gray-900">{t('faq.items.item3.title')}</dt>
                <dd className="mt-4 text-gray-600">{t('faq.items.item3.description')}</dd>
              </div>
              <div>
                <dt className="font-semibold text-gray-900">{t('faq.items.item4.title')}</dt>
                <dd className="mt-4 text-gray-600">{t('faq.items.item4.description')}</dd>
              </div>
            </dl>
          </div>
        </div>
      </div>

      {/* Logos Section */}
      <div className="bg-white py-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mx-auto max-w-2xl lg:text-center"
          >
            <h2 className="text-lg font-semibold leading-8 tracking-tight text-indigo-600">
              {t('logos.title')}
            </h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t('logos.subtitle')}
            </p>
          </motion.div>
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="mx-auto mt-10 grid max-w-lg grid-cols-4 items-center gap-x-8 gap-y-10 sm:max-w-xl sm:grid-cols-6 sm:gap-x-10 lg:mx-0 lg:max-w-none lg:grid-cols-5"
          >
            {[
              { name: 'Shopify', logo: '/logos/shopify.svg' },
              { name: 'WooCommerce', logo: '/logos/woocommerce.svg' },
              { name: 'PrestaShop', logo: '/logos/prestashop.svg' },
              { name: 'Magento', logo: '/logos/magento.svg' },
              { name: 'OpenCart', logo: '/logos/opencart.svg' }
            ].map((partner) => (
              <motion.div
                key={partner.name}
                variants={fadeInUp}
                className="col-span-2 max-h-12 w-full object-contain lg:col-span-1"
              >
                <img
                  className="h-12 w-auto grayscale hover:grayscale-0 transition-all duration-300"
                  src={partner.logo}
                  alt={partner.name}
                />
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Contact Section */}
      <div id="contact" className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="grid grid-cols-1 gap-x-8 gap-y-10 lg:grid-cols-2"
          >
            {/* Contact Info */}
            <motion.div
              variants={fadeInUp}
              className="lg:pr-8"
            >
              <h2 className="text-3xl font-bold tracking-tight text-gray-900">
                {t('contact.title')}
              </h2>
              <p className="mt-4 text-lg text-gray-600">
                {t('contact.description')}
              </p>
              <dl className="mt-8 space-y-6">
                <div>
                  <dt className="text-base font-semibold text-gray-900">{t('contact.items.email.title')}</dt>
                  <dd className="mt-1 text-base text-gray-600">{t('contact.items.email.description')}</dd>
                </div>
                <div>
                  <dt className="text-base font-semibold text-gray-900">{t('contact.items.phone.title')}</dt>
                  <dd className="mt-1 text-base text-gray-600">{t('contact.items.phone.description')}</dd>
                </div>
                <div>
                  <dt className="text-base font-semibold text-gray-900">{t('contact.items.support.title')}</dt>
                  <dd className="mt-1 text-base text-gray-600">
                    {t('contact.items.support.description')}
                  </dd>
                </div>
              </dl>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              variants={fadeInUp}
              className="bg-white shadow-lg rounded-lg p-8"
            >
              <form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                    {t('contact.form.name')}
                  </label>
                  <input
                    type="text"
                    name="name"
                    id="name"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    {t('contact.form.email')}
                  </label>
                  <input
                    type="email"
                    name="email"
                    id="email"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700">
                    {t('contact.form.message')}
                  </label>
                  <textarea
                    name="message"
                    id="message"
                    rows={4}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div>
                  <button
                    type="submit"
                    className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    {t('contact.form.submit')}
                  </button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Quick Start Guide */}
      <div className="bg-gray-50 py-24 sm:py-32">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="mx-auto max-w-2xl lg:text-center">
            <h2 className="text-base font-semibold leading-7 text-indigo-600">{t('guide.title')}</h2>
            <p className="mt-2 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
              {t('guide.subtitle')}
            </p>
          </div>
          <div className="mx-auto mt-16 max-w-2xl sm:mt-20 lg:mt-24 lg:max-w-none">
            <div className="grid grid-cols-1 gap-y-8 gap-x-6 sm:grid-cols-3">
              <div className="relative pl-9">
                <div className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600">
                  <span className="text-lg font-semibold text-white">1</span>
                </div>
                <h3 className="text-lg font-semibold leading-8 text-gray-900">{t('guide.items.item1.title')}</h3>
                <p className="mt-2 text-base leading-7 text-gray-600">
                  {t('guide.items.item1.description')}
                </p>
              </div>
              <div className="relative pl-9">
                <div className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600">
                  <span className="text-lg font-semibold text-white">2</span>
                </div>
                <h3 className="text-lg font-semibold leading-8 text-gray-900">{t('guide.items.item2.title')}</h3>
                <p className="mt-2 text-base leading-7 text-gray-600">
                  {t('guide.items.item2.description')}
                </p>
              </div>
              <div className="relative pl-9">
                <div className="absolute left-0 top-0 flex h-8 w-8 items-center justify-center rounded-lg bg-indigo-600">
                  <span className="text-lg font-semibold text-white">3</span>
                </div>
                <h3 className="text-lg font-semibold leading-8 text-gray-900">{t('guide.items.item3.title')}</h3>
                <p className="mt-2 text-base leading-7 text-gray-600">
                  {t('guide.items.item3.description')}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Blog Section */}
      <div id="blog">
        <BlogSection />
      </div>

      {/* Navigation flottante */}
      <FloatingNav />

      {/* Live Chat */}
      <LiveChat />

      {/* CTA Section */}
      <div className="bg-white">
        <div className="mx-auto max-w-7xl py-24 sm:px-6 sm:py-32 lg:px-8">
          <div className="relative isolate overflow-hidden bg-gray-900 px-6 py-24 text-center shadow-2xl sm:rounded-3xl sm:px-16">
            <h2 className="mx-auto max-w-2xl text-3xl font-bold tracking-tight text-white sm:text-4xl">
              {t('cta.title')}
            </h2>
            <p className="mx-auto mt-6 max-w-xl text-lg leading-8 text-gray-300">
              {t('cta.description')}
            </p>
            <div className="mt-10 flex items-center justify-center gap-x-6">
              <Link
                to="/login"
                className="rounded-md bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-900 shadow-sm hover:bg-gray-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white"
              >
                {t('cta.login')}
              </Link>
              <Link
                to="/register"
                className="text-sm font-semibold leading-6 text-white"
              >
                {t('cta.register')} <span aria-hidden="true">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPage;
